import { core, type Decimal } from '@map-my-money/engine';
import { readableDate, readableMonth, regionOf, type Region } from '@map-my-money/views';

export { money, percentChange, regionOf, round1, type Region } from '@map-my-money/views';

type PlanLike = Parameters<typeof regionOf>[0];

/**
 * The plan document carries no currency: a US plan is in dollars, any other in pounds. Structured
 * output always carries plain decimal strings; only the human text summaries add a symbol.
 */
export const planCurrency = (plan: PlanLike): 'GBP' | 'USD' => (regionOf(plan) === 'us' ? 'USD' : 'GBP');

const SYMBOL: Readonly<Record<Region, string>> = { uk: '£', us: '$' };

const trim = (n: number, digits: number): string => {
  const fixed = n.toFixed(digits);
  return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
};

const toNumber = (value: Decimal | number | string): number =>
  typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : core.toDouble(value);

/** Money in tool text, in the plan's currency. */
export interface TextMoney {
  readonly region: Region;
  /** "£1.02M", "$45.3K", "£850", "−£1.2K". */
  compact(value: Decimal | number | string): string;
  /** A signed change: "+£30.1K", "−$1.2K", "no change". */
  signedCompact(value: Decimal): string;
  /** "£314,762": the exact amount, for the few numbers a text leads with. */
  exact(value: string | number): string;
}

export function textMoney(region: Region): TextMoney {
  const symbol = SYMBOL[region];
  const compact = (value: Decimal | number | string): string => {
    const n = toNumber(value);
    const sign = n < 0 ? '−' : '';
    const a = Math.abs(n);
    if (a >= 1_000_000) return `${sign}${symbol}${trim(a / 1_000_000, a >= 10_000_000 ? 1 : 2)}M`;
    if (a >= 999_500) return `${sign}${symbol}${trim(a / 1_000_000, 2)}M`;
    if (a >= 10_000) return `${sign}${symbol}${trim(a / 1_000, a >= 100_000 ? 0 : 1)}K`;
    if (a >= 1_000) return `${sign}${symbol}${trim(a / 1_000, 2)}K`;
    return `${sign}${symbol}${trim(a, a >= 100 ? 0 : 2)}`;
  };
  return {
    region,
    compact,
    signedCompact: (value) => {
      const n = core.toDouble(value);
      if (Math.abs(n) < 0.005) return 'no change';
      return n > 0 ? `+${compact(n)}` : compact(n);
    },
    exact: (value) => {
      const n = typeof value === 'number' ? value : Number(value);
      const sign = n < 0 ? '−' : '';
      const whole = String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${sign}${symbol}${whole}`;
    },
  };
}

export const textMoneyFor = (plan: PlanLike): TextMoney => textMoney(regionOf(plan));

/** Dates people read in tool text: "10 Oct 2026"; ISO dates stay in structured data. */
export const day = (iso: string): string => readableDate(iso);

/** "Feb 2031", for goal and payoff months (goals are dated at the start of a month). */
export const month = (iso: string): string => readableMonth(iso);

/** "3 years 2 months", "5 months", "now". */
export function span(months: number): string {
  if (months <= 0) return 'now';
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [y > 0 ? `${y} year${y === 1 ? '' : 's'}` : '', m > 0 ? `${m} month${m === 1 ? '' : 's'}` : ''].filter(Boolean);
  return parts.join(' ');
}
